import React, { useEffect, useRef } from 'react';

// The BBB seal token and loader script the Better Business Bureau issued for
// this business. The loader finds the <bbbseal class="bbbseal ruhzbam"> element
// on the page and renders the accredited-business seal (a linked badge image)
// into it.
const BBB_SEAL_TOKEN = 'ruhzbam';
const BBB_SEAL_SRC =
  'https://seal-boston.bbb.org/v3/logo/ruhzbam/bbb-572389.js';

// Detect react-snap's puppeteer UA so the seal isn't injected into the
// prerendered HTML (it should only run for real, hydrated visitors — the same
// guard used by MetaPixel/MicrosoftClarity).
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

interface BBBSealProps {
  /** Optional class applied to the wrapper element for layout/spacing. */
  className?: string;
}

/**
 * Renders the Better Business Bureau accredited-business seal.
 *
 * The official BBB embed is a <bbbseal> custom element plus an inline loader
 * <script>. A raw inline script doesn't belong in this React/Vite app (it would
 * also trip the Content-Security-Policy), so this component recreates the embed
 * imperatively and loads the BBB script the same deferred, guarded way the other
 * third-party tags are handled:
 *
 *  - skipped during react-snap prerender,
 *  - loaded off the critical path (requestIdleCallback / timeout) since the seal
 *    lives below the fold in the footer,
 *  - guarded against double-injection, and
 *  - torn down on unmount so a later remount re-renders the seal cleanly.
 *
 * NOTE: the BBB host (seal-boston.bbb.org / *.bbb.org) must stay whitelisted in
 * the Content-Security-Policy — script-src/connect-src in vercel.json and
 * docker/default.conf — or the loader will be blocked.
 */
export const BBBSeal: React.FC<BBBSealProps> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (IS_PRERENDERING) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    // (Re)create the seal mount node. Clearing first keeps a single <bbbseal>
    // node across React 18 StrictMode double-invokes and route remounts.
    container.innerHTML = '';
    const seal = document.createElement('bbbseal');
    seal.className = `bbbseal ${BBB_SEAL_TOKEN}`;
    container.appendChild(seal);

    let injected = false;
    let createdScript: HTMLScriptElement | null = null;

    const inject = () => {
      if (injected) return;
      injected = true;
      try {
        // Don't add a second loader if one is already on the page.
        if (document.querySelector(`script[src="${BBB_SEAL_SRC}"]`)) {
          return;
        }
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = BBB_SEAL_SRC;
        script.onerror = () => {
          console.warn('BBB seal script failed to load');
        };
        document.head.appendChild(script);
        createdScript = script;
      } catch (e) {
        console.warn('BBB seal initialization failed:', e);
      }
    };

    // Defer the loader off the critical path: idle callback (with timeout) or a
    // hard fallback timer — whichever fires first.
    const w = window as unknown as {
      requestIdleCallback?: (
        // eslint-disable-next-line no-unused-vars
        _cb: () => void,
        // eslint-disable-next-line no-unused-vars
        _opts?: { timeout: number }
      ) => number;
      // eslint-disable-next-line no-unused-vars
      cancelIdleCallback?: (_handle: number) => void;
    };

    let idleHandle: number | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(inject, { timeout: 2500 });
    }
    const timeoutId = window.setTimeout(inject, 2000);

    return () => {
      window.clearTimeout(timeoutId);
      if (
        idleHandle !== undefined &&
        typeof w.cancelIdleCallback === 'function'
      ) {
        w.cancelIdleCallback(idleHandle);
      }
      // Remove only the loader this instance added and clear the mount node so a
      // remount re-runs the loader and re-renders the seal.
      if (createdScript && createdScript.parentNode) {
        createdScript.parentNode.removeChild(createdScript);
      }
      container.innerHTML = '';
    };
  }, []);

  return <div ref={containerRef} className={className} />;
};

export default BBBSeal;
